import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { ProductService } from "@domain/product/product.service";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectImageService } from "@domain/project/project-image.service";
import { ProjectMigrationService } from "@domain/project/project-migration.service";
import { ProjectService } from "@domain/project/project.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { catchError, forkJoin, Observable, of, switchMap, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ProjectResolver {
  private projectService = inject(ProjectService);
  private productService = inject(ProductService);
  private projectImageService = inject(ProjectImageService);
  private projectFloorplanService = inject(ProjectFloorplanService);
  private projectMigrationService = inject(ProjectMigrationService);
  private apiService = inject(ProjectApiService);
  private router = inject(Router);
  private toastHelper = inject(ToastHelper);

  resolve(route: ActivatedRouteSnapshot): Observable<ScopedProject | undefined> {
    const routeId = route.params["id"];

    return this.projectService.getLocalProjectById(routeId).pipe(
      switchMap((project) => {
        if (project) {
          return this.loadProjectData(project);
        }

        if (!navigator.onLine) {
          this.toastHelper.error(
            $localize`:@@toast.project.openFailed.header:Projekt konnte nicht geöffnet werden`,
            $localize`:@@toast.project.openFailed.description:Das Projekt konnte nicht geöffnet werden. Es ist möglicherweise fehlerhaft oder wurde entfernt.`,
          );
          this.router.navigate([""]);
          return of(undefined);
        }

        return this.importFromCloud(routeId);
      }),
    );
  }

  /**
   * Imports a project that is not stored locally (yet).
   *
   * Links created inside the app carry the local project id, so the cloud id is looked up
   * first. Links created by the API - e.g. the project link the Forza integration returns -
   * carry the cloud id directly. For those the lookup misses, so the route parameter is
   * retried as a cloud id before the project is reported as not found.
   */
  private importFromCloud(routeId: string): Observable<ScopedProject | undefined> {
    return this.projectService
      .getProjectCloudIdByLocalId(routeId)
      .pipe(
        switchMap((cloudId) =>
          cloudId
            ? this.loadAndStoreProject(cloudId)
            : this.loadAndStoreProject(routeId).pipe(catchError(() => this.reportProjectNotFound())),
        ),
      );
  }

  private loadAndStoreProject(cloudId: string): Observable<ScopedProject | undefined> {
    return forkJoin({
      project: this.projectService.loadProjectFromCloud(cloudId),
      metadata: this.apiService.getMetadata(cloudId),
    }).pipe(
      switchMap(({ project, metadata }) =>
        this.projectService
          .createProject(project, metadata)
          .pipe(
            switchMap((savedProject) =>
              this.projectService
                .updateUserWritePermission(savedProject)
                .pipe(switchMap(() => this.loadProjectData(savedProject))),
            ),
          ),
      ),
    );
  }

  private reportProjectNotFound(): Observable<undefined> {
    this.toastHelper.error(
      $localize`:@@toast.project.cloudIdLookupFailed.header:Projekt konnte nicht gefunden werden`,
      $localize`:@@toast.project.cloudIdLookupFailed.description:Das Projekt konnte nicht geöffnet werden. Es ist möglicherweise noch nicht synchronisiert worden.`,
    );
    this.router.navigate([""]);
    return of(undefined);
  }

  private loadProjectData(project: ScopedProject): Observable<ScopedProject | undefined> {
    const refreshFromCloud$ = project.cloudId
      ? this.projectImageService.getProjectImages(project.cloudId, project.id).pipe(
          catchError(() => of(null)),
          switchMap(() => this.projectFloorplanService.getProjectFloorplans(project).pipe(catchError(() => of(null)))),
        )
      : of(null);

    return refreshFromCloud$.pipe(
      switchMap(() => this.projectMigrationService.runMigrations(project, (p) => this.projectService.update(p))),
      tap(() => {
        this.productService.updateFilterCountry(project.country);
        this.projectService.createProjectTemplate(project.country);
      }),
    );
  }
}
